<script lang="ts">
export default {
  name: 'EditableField',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { ParsedField } from '@/types/field';
import type { FieldOverride } from '@/types/ai';
import { Loader2, Undo2, AlertCircle, AlertTriangle } from '@lucide/vue';
import { TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal } from 'reka-ui';
import { formatCurrency } from '@/utilities/Money';
import { computeCaretAfterReformat } from '@/utilities/CurrencyInput';

const props = defineProps<{
  field: ParsedField;
  override: FieldOverride | null;
  isPending: boolean;
  error: string | null;
  hasLocalChange?: boolean;
  // The reviewer has undone a prior saved override on this field; show the LLM original.
  reverted?: boolean;
  localChangeValue?: string | number | boolean;
  disabled?: boolean;
  disabledReason?: string;
  currencyFormat?: boolean;
}>();

const emit = defineEmits<{
  'update:value': [value: string | number | boolean];
  revert: [];
  'clear-error': [];
  'clear-pending': [];
}>();

// Local state for the input value
const localValue = ref<string | number | boolean>('');

// Tracks whether this input is the active edit target. Prevents an external
// value sync (from another view of the same field) from overwriting the user
// while they are typing.
const isFocused = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);

// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Pull the current value from the most-specific source: an unsaved local
// change wins over a saved override, which wins over the original AI value.
const initializeValue = () => {
  if (props.reverted) {
    // Override undone: fall back to the LLM original (override.originalValue, else AI value).
    const original = props.override ? props.override.originalValue : props.field.value;
    localValue.value = (original ?? '') as string | number | boolean;
  } else if (props.localChangeValue !== undefined) {
    localValue.value = props.localChangeValue;
  } else if (props.override && props.override.overrideValue !== null) {
    localValue.value = props.override.overrideValue;
  } else {
    localValue.value = props.field.value as string | number | boolean;
  }
};

// Initialize on mount and when field/override/revert state changes
watch(
  () => [props.field.path, props.override, props.reverted],
  () => {
    initializeValue();
  },
  { immediate: true },
);

// Sync to externally-driven changes (e.g., another mounted EditableField for
// the same path updated the shared pending-overrides map). Skip while we are
// the active editor to avoid clobbering keystrokes mid-debounce.
watch(
  () => props.localChangeValue,
  (newValue) => {
    if (isFocused.value) return;
    if (newValue !== localValue.value) {
      initializeValue();
    }
  },
);

// Computed
const hasOverride = computed(() => props.override !== null);
const hasUnsavedChange = computed(() => props.hasLocalChange === true);
const hasAnyChange = computed(() => hasOverride.value || hasUnsavedChange.value);

const originalValue = computed(() => {
  if (props.override) {
    return props.override.originalValue;
  }
  return props.field.value;
});

const displayOriginalValue = computed(() => {
  const val = originalValue.value;
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (props.currencyFormat && (props.field.type === 'number' || props.field.type === 'string')) {
    const formatted = formatCurrency(val as number | string, true);
    if (formatted) return formatted;
  }
  return String(val);
});

const isCurrencyInput = computed(
  () => props.currencyFormat === true && (props.field.type === 'number' || props.field.type === 'string'),
);

const displayValue = computed<string | number | boolean>(() => {
  if (!isCurrencyInput.value) return localValue.value;
  const v = localValue.value;
  if (v === '' || v === null || v === undefined) return '';
  const num = typeof v === 'number' ? v : Number(v);
  if (isNaN(num) || !isFinite(num)) return v;
  return formatCurrency(num, true);
});

// Handle input changes with debounce
const handleInputChange = (event: Event) => {
  if (props.disabled) return;
  const target = event.target as HTMLInputElement;
  let newValue: string | number | boolean;
  // For currency fields with an empty input, keep '' locally so the user
  // sees an empty box (not "$0") while typing. The debounce step below
  // coerces '' → 0 before emitting for number-typed fields.
  let localNewValue: string | number | boolean;

  if (isCurrencyInput.value) {
    const cleaned = target.value.replace(/[^0-9.-]/g, '');
    if (cleaned === '') {
      // Empty input: '' locally for both string- and number-typed fields.
      localNewValue = '';
      newValue = props.field.type === 'string' ? '' : 0;
    } else {
      let parsedNumber: number;
      if (cleaned === '-' || cleaned === '.') {
        parsedNumber = 0;
      } else {
        const parsed = Number(cleaned);
        parsedNumber = isNaN(parsed) ? 0 : parsed;
      }
      newValue = props.field.type === 'string' ? String(parsedNumber) : parsedNumber;
      localNewValue = newValue;
    }
  } else if (props.field.type === 'number') {
    newValue = target.value === '' ? 0 : Number(target.value);
    localNewValue = newValue;
  } else {
    newValue = target.value;
    localNewValue = newValue;
  }

  localValue.value = localNewValue;

  // Formatting while focused causes the browser to collapse the caret on
  // each keystroke; restore it relative to the digit the user just typed.
  if (isCurrencyInput.value && inputRef.value) {
    const typedValue = target.value;
    const caretIndex = target.selectionStart ?? typedValue.length;
    nextTick(() => {
      const el = inputRef.value;
      if (!el) return;
      const newCaret = computeCaretAfterReformat(typedValue, caretIndex, el.value);
      el.setSelectionRange(newCaret, newCaret);
    });
  }

  // Clear any existing error when user starts typing
  if (props.error) {
    emit('clear-error');
  }

  // Debounce the save
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    const currentStored = props.override && props.override.overrideValue !== null ? props.override.overrideValue : props.field.value;
    if (newValue !== currentStored) {
      emit('update:value', newValue);
    } else if (props.hasLocalChange) {
      // User typed the original value back — drop the pending change.
      emit('clear-pending');
    }
  }, 500);
};

// Handle boolean toggle
const handleToggleChange = (event: Event) => {
  if (props.disabled) return;
  const target = event.target as HTMLInputElement;
  const newValue = target.checked;
  localValue.value = newValue;

  // Clear any existing error
  if (props.error) {
    emit('clear-error');
  }

  // Emit immediately for boolean (no debounce needed)
  emit('update:value', newValue);
};

// Handle revert
const handleRevert = () => {
  localValue.value = originalValue.value as string | number | boolean;
  emit('revert');
};
</script>

<template>
  <div class="flex w-full flex-col gap-1">
    <div class="flex items-center gap-2">
      <!-- String/Number Input -->
      <div
        v-if="field.type === 'string' || field.type === 'number'"
        class="relative flex-1">
        <TooltipProvider>
          <TooltipRoot :disabled="!hasAnyChange">
            <TooltipTrigger as-child>
              <input
                ref="inputRef"
                :type="isCurrencyInput ? 'text' : field.type === 'number' ? 'number' : 'text'"
                :inputmode="isCurrencyInput ? 'decimal' : undefined"
                :value="displayValue"
                @input="handleInputChange"
                @focus="isFocused = true"
                @blur="isFocused = false"
                :disabled="isPending || disabled"
                :class="[
                  'w-full rounded border px-2 py-0.5 text-xs transition-colors outline-none',
                  'focus:ring-1',
                  hasUnsavedChange
                    ? 'border-amber-500 bg-amber-50 focus:border-amber-600 focus:ring-amber-500'
                    : hasOverride
                      ? 'border-green-500 bg-green-50 focus:border-green-600 focus:ring-green-500'
                      : 'border-slate-300 bg-white focus:border-blue-500 focus:ring-blue-500',
                  error ? 'border-red-500 bg-red-50' : '',
                  isPending ? 'opacity-60' : '',
                  disabled ? 'cursor-not-allowed opacity-50' : '',
                ]" />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                v-if="hasAnyChange"
                side="top"
                :side-offset="5"
                class="z-50 max-w-xs rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg">
                <p class="font-medium">Original value:</p>
                <p class="mt-1 wrap-break-word text-slate-300">{{ displayOriginalValue }}</p>
              </TooltipContent>
            </TooltipPortal>
          </TooltipRoot>
        </TooltipProvider>
      </div>

      <!-- Boolean Toggle -->
      <div
        v-else-if="field.type === 'boolean'"
        class="flex flex-1 items-center gap-2">
        <TooltipProvider>
          <TooltipRoot :disabled="!hasAnyChange">
            <TooltipTrigger as-child>
              <label
                :class="[
                  'flex items-center gap-1.5 rounded border px-2 py-0.5',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  hasUnsavedChange ? 'border-amber-500 bg-amber-50' : hasOverride ? 'border-green-500 bg-green-50' : 'border-slate-300 bg-white',
                  isPending ? 'opacity-60' : '',
                ]">
                <input
                  type="checkbox"
                  :checked="localValue === true"
                  @change="handleToggleChange"
                  @focus="isFocused = true"
                  @blur="isFocused = false"
                  :disabled="isPending || disabled"
                  class="toggle toggle-primary toggle-xs" />
                <span class="text-xs text-slate-600">
                  {{ localValue ? 'true' : 'false' }}
                </span>
              </label>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                v-if="hasAnyChange"
                side="top"
                :side-offset="5"
                class="z-50 max-w-xs rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg">
                <p class="font-medium">Original value:</p>
                <p class="mt-1 text-slate-300">{{ displayOriginalValue }}</p>
              </TooltipContent>
            </TooltipPortal>
          </TooltipRoot>
        </TooltipProvider>
      </div>

      <!-- Loading Spinner -->
      <Loader2
        v-if="isPending"
        class="h-3.5 w-3.5 shrink-0 animate-spin text-blue-500" />

      <!-- Revert Button (shown when there's a local change or saved override) -->
      <button
        v-if="hasAnyChange && !isPending && !disabled"
        @click="handleRevert"
        class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        :title="hasUnsavedChange ? 'Discard change' : 'Revert to original value'">
        <Undo2 class="h-3.5 w-3.5" />
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="flex items-center gap-1 text-xs text-red-600">
      <AlertCircle class="h-3 w-3" />
      <span>{{ error }}</span>
    </div>

    <!-- Disabled Reason (e.g. PolicySource restriction) -->
    <div
      v-if="disabled && disabledReason"
      class="flex items-center gap-1 text-xs text-amber-700">
      <AlertTriangle class="h-3 w-3" />
      <span>{{ disabledReason }}</span>
    </div>
  </div>
</template>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
